import { BUILT_IN_MASCOT_LOOK, GENERAL_GROUP_ID, GENERAL_GROUP_NAME } from './mascotLooks.js';
/** The search applies this long after the last keystroke, so a gallery of 2,000 is not filtered on every letter. */
export const MASCOT_SEARCH_DEBOUNCE_MS = 150;
export const MASCOT_SORT_LABELS = {
    gallery: 'Gallery order',
    name: 'Name A–Z',
    rating: 'Highest rated',
    picks: 'Most picked',
    newest: 'Newest',
};
export const MASCOT_ANIMATION_LABELS = {
    all: 'All',
    animated: 'Animated',
    still: 'Not animated',
};
/** The animation filter's choices in the order the gallery offers them. */
export const MASCOT_ANIMATION_CHOICES = ['all', 'animated', 'still'];
export const MASCOT_NO_FILTERS = Object.freeze({
    query: '', categories: new Set(), mine: false, day: false, featured: false, unrated: false, animation: 'all', sort: 'gallery',
});
/** Whether the search or a filter narrows the gallery (the sort alone does not): then every matching group shows open and empty ones are hidden. */
export function mascotFiltersNarrow(filters) {
    return normalizeMascotText(filters.query) !== '' || filters.categories.size > 0 || filters.mine || filters.day || filters.featured || filters.unrated || filters.animation !== 'all';
}
/** Text as the search compares it: lower-case, accents removed, runs of spaces as one. */
export function normalizeMascotText(text) {
    return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}
/** The words of a search, normalized; [] for an empty search. */
export function mascotSearchWords(query) {
    const normalized = normalizeMascotText(query);
    return normalized ? normalized.split(' ') : [];
}
/** The text a tile is searched by: its name and its group's name. */
function searchableText(tile) {
    return normalizeMascotText(`${tile.name} ${tile.category?.name ?? GENERAL_GROUP_NAME}`);
}
/** Whether every word of the search appears in the tile's name or its group's name. */
export function mascotTileMatches(tile, words) {
    if (words.length === 0)
        return true;
    const text = searchableText(tile);
    return words.every((word) => text.includes(word));
}
/** The tiles the search and the filters keep, in the order given. */
export function filterMascotTiles(tiles, filters, context) {
    const words = mascotSearchWords(filters.query);
    return tiles.filter((tile) => {
        if (!mascotTileMatches(tile, words))
            return false;
        if (filters.categories.size > 0 && !filters.categories.has(tile.category?.id ?? GENERAL_GROUP_ID))
            return false;
        const rating = tile.id === BUILT_IN_MASCOT_LOOK ? undefined : context.ratingOf(tile.id);
        if (filters.mine && !(tile.id === context.pick || (rating !== undefined && rating.mine !== null)))
            return false;
        if (filters.day && !(tile.dayOutfit || tile.today))
            return false;
        if (filters.featured && !tile.featured)
            return false;
        // "Not yet rated": an outfit with stars to give that the person has not given; the built-in Gyro has none to give.
        if (filters.unrated && !(rating !== undefined && rating.mine === null))
            return false;
        // Animation: "Animated" keeps the outfits whose `hasActions` is true, "Not animated" every other one (the built-in
        // Gyro included: it follows the 'default' binding, and counts as not animated while gyro-svc says nothing).
        if (filters.animation === 'animated' && tile.hasActions !== true)
            return false;
        if (filters.animation === 'still' && tile.hasActions === true)
            return false;
        return true;
    });
}
/** Larger first; a tile without the value goes last; ties keep the order given (sort is stable). */
function byDescending(value) {
    return (a, b) => {
        const left = value(a);
        const right = value(b);
        if (left === right)
            return 0;
        if (left === undefined)
            return 1;
        if (right === undefined)
            return -1;
        return right - left;
    };
}
/** The tiles in the chosen order: the built-in Gyro first, then the outfits; 'gallery' keeps the order given. */
export function sortMascotTiles(tiles, sort, ratingOf) {
    if (sort === 'gallery')
        return [...tiles];
    const builtIn = tiles.filter((tile) => tile.id === BUILT_IN_MASCOT_LOOK);
    const outfits = tiles.filter((tile) => tile.id !== BUILT_IN_MASCOT_LOOK);
    const compare = sort === 'name'
        ? (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.id.localeCompare(b.id)
        : sort === 'rating'
            // Everyone's average first (none last), then how many rated it, then the gallery's order.
            ? (a, b) => compareStats(ratingOf(a.id) ?? a.rating, ratingOf(b.id) ?? b.rating)
            : sort === 'picks' ? byDescending((tile) => tile.picks)
                : byDescending((tile) => tile.addedAtMs);
    return [...builtIn, ...outfits.sort(compare)];
}
function compareStats(left, right) {
    const leftAverage = left?.average ?? null;
    const rightAverage = right?.average ?? null;
    if (leftAverage !== rightAverage) {
        if (leftAverage === null)
            return 1;
        if (rightAverage === null)
            return -1;
        return rightAverage - leftAverage;
    }
    return (right?.count ?? 0) - (left?.count ?? 0);
}
/** The categories the gallery's outfits are in now, with how many each holds, General first (when any outfit has no category). */
export function mascotCategoriesPresent(tiles) {
    const counts = new Map();
    for (const tile of tiles) {
        if (tile.id === BUILT_IN_MASCOT_LOOK)
            continue;
        const id = tile.category?.id ?? GENERAL_GROUP_ID;
        const known = counts.get(id);
        if (known)
            known.count += 1;
        else
            counts.set(id, { id, name: tile.category?.name ?? GENERAL_GROUP_NAME, count: 1 });
    }
    const general = counts.get(GENERAL_GROUP_ID);
    const named = [...counts.values()].filter((choice) => choice.id !== GENERAL_GROUP_ID)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.id.localeCompare(b.id));
    return general ? [general, ...named] : named;
}
/** The sorts the data supports: by name always; by rating only with ratings, by picks and by date only when gyro-svc sent them. */
export function mascotSortsAvailable(tiles, ratingsShown) {
    const outfits = tiles.filter((tile) => tile.id !== BUILT_IN_MASCOT_LOOK);
    const sorts = ['gallery', 'name'];
    if (ratingsShown || outfits.some((tile) => tile.rating))
        sorts.push('rating');
    if (outfits.some((tile) => tile.picks !== undefined))
        sorts.push('picks');
    if (outfits.some((tile) => tile.addedAtMs !== undefined))
        sorts.push('newest');
    return sorts;
}
/**
 * Which of the narrowing filters the data supports: day outfits and a featured outfit only when there are any; "Not yet
 * rated" only where ratings show; the animation filter only while gyro-svc says which outfits have one (`hasActions` on
 * any tile: absent from an older gyro-svc and while mascot animations are off, when every outfit would read as not animated).
 */
export function mascotFiltersAvailable(tiles, ratingsShown) {
    return {
        day: tiles.some((tile) => tile.dayOutfit || tile.today),
        featured: tiles.some((tile) => tile.featured),
        unrated: ratingsShown,
        animation: tiles.some((tile) => tile.hasActions !== undefined),
    };
}
//# sourceMappingURL=mascotGalleryFilters.js.map